import React, { useEffect, useRef, useState, useMemo } from 'react';
import type { LottieRefCurrentProps } from 'lottie-react';
import Lottie from 'lottie-react';
import { getCustomLottie } from '../services/confirmationDesignSettings';

/**
 * v5 — Animation Lottie pour l'écran de confirmation.
 *
 * Optimisations anti-lag :
 *  - Lazy import du JSON par défaut (chargé UNIQUEMENT si la prop active=true)
 *  - Vérifie d'abord s'il y a un Lottie custom en IndexedDB
 *  - Cache en mémoire (chargé 1× max par session)
 *  - Pause automatique quand le composant est démonté
 *  - Si loop=false, l'animation joue 1 seule fois puis se fige (économie CPU)
 *  - rendererSettings.progressiveLoad=true pour rendu fluide
 *  - Skeleton léger pendant le chargement
 *  - Écoute l'event 'customLottieUpdated' pour rafraîchir le cache si l'admin upload
 */

// Cache module-level — partagé entre les instances du composant
let cachedAnimationData: object | null = null;
let cachedAnimationPromise: Promise<object> | null = null;
let cachedSource: 'default' | 'custom' | null = null;

/**
 * Charge l'animation : Lottie custom de l'admin en priorité, sinon le défaut.
 */
const loadAnimation = async (): Promise<object> => {
  // Vérifier d'abord le Lottie custom en IndexedDB
  try {
    const customLottie = await getCustomLottie();
    if (customLottie) {
      cachedAnimationData = customLottie;
      cachedSource = 'custom';
      return customLottie;
    }
  } catch (e) {
    console.warn('[LottieCheckout] Échec chargement custom, fallback sur défaut', e);
  }

  // Sinon, charger le Lottie par défaut via lazy import
  if (cachedAnimationData && cachedSource === 'default') {
    return cachedAnimationData;
  }
  if (cachedAnimationPromise) return cachedAnimationPromise;

  cachedAnimationPromise = import('../animations/checkout-confirmation.json')
    .then((mod) => {
      cachedAnimationData = (mod as any).default || mod;
      cachedSource = 'default';
      return cachedAnimationData!;
    })
    .catch((err) => {
      console.error('[LottieCheckout] Erreur chargement animation par défaut:', err);
      cachedAnimationPromise = null; // permet de réessayer
      throw err;
    });
  return cachedAnimationPromise;
};

/** Invalide le cache — appelé quand l'admin uploade ou supprime un Lottie custom */
const invalidateCache = () => {
  cachedAnimationData = null;
  cachedAnimationPromise = null;
  cachedSource = null;
};

interface LottieCheckoutProps {
  active: boolean;            // si false, ne charge même pas le JSON
  loop?: boolean;             // false par défaut (joue 1 fois)
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const SIZE_MAP: Record<NonNullable<LottieCheckoutProps['size']>, { width: number; height: number }> = {
  small: { width: 140, height: 100 },
  medium: { width: 220, height: 160 },
  large: { width: 300, height: 220 },
};

export const LottieCheckout: React.FC<LottieCheckoutProps> = React.memo(
  ({ active, loop = false, size = 'medium', className }) => {
    const [animationData, setAnimationData] = useState<object | null>(cachedAnimationData);
    const [error, setError] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const lottieRef = useRef<LottieRefCurrentProps>(null);
    const dimensions = useMemo(() => SIZE_MAP[size], [size]);

    // Charger l'animation seulement quand active=true
    useEffect(() => {
      if (!active) return;

      let mounted = true;
      setError(false);
      loadAnimation()
        .then((data) => {
          if (mounted) setAnimationData(data);
        })
        .catch(() => {
          if (mounted) setError(true);
        });

      return () => {
        mounted = false;
      };
    }, [active, reloadKey]);

    // Écouter les updates du Lottie custom (upload/suppression côté admin)
    useEffect(() => {
      const handleUpdate = () => {
        invalidateCache();
        setAnimationData(null);
        setReloadKey((k) => k + 1);
      };
      window.addEventListener('customLottieUpdated', handleUpdate);
      return () => {
        window.removeEventListener('customLottieUpdated', handleUpdate);
      };
    }, []);

    // Cleanup au démontage : stopper et libérer
    useEffect(() => {
      return () => {
        try {
          lottieRef.current?.stop();
          lottieRef.current?.destroy();
        } catch (_) {
          // ignore — peut déjà être démonté
        }
      };
    }, []);

    if (!active) return null;

    // Skeleton léger pendant chargement (pas de spinner agressif)
    if (!animationData && !error) {
      return (
        <div
          className={className}
          style={{
            width: dimensions.width,
            height: dimensions.height,
            margin: '0 auto',
          }}
          aria-hidden="true"
        />
      );
    }

    // Si erreur, on n'affiche rien (le popup reste fonctionnel)
    if (error || !animationData) return null;

    return (
      <div
        className={className}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          margin: '0 auto',
          pointerEvents: 'none', // n'interfère pas avec les clics
        }}
      >
        <Lottie
          lottieRef={lottieRef}
          animationData={animationData}
          loop={loop}
          autoplay={true}
          style={{ width: '100%', height: '100%' }}
          rendererSettings={{
            preserveAspectRatio: 'xMidYMid meet',
            progressiveLoad: true,
          }}
        />
      </div>
    );
  }
);

LottieCheckout.displayName = 'LottieCheckout';

export default LottieCheckout;
